import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  DirectionsCar as CarIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Public as PublicIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ATSLandSystemDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/ats-system');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to ATS System"
        >
          Back to ATS
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            ATS-Land (ATS-L)
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Land Border Vehicle Targeting Subsystem
          </Typography>
        </Box>
      </Box>

      {/* System Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Subsystem Overview
          </Typography>
          <Typography variant="body1" paragraph>
            ATS-Land (ATS-L) is the specialized targeting subsystem focused on private vehicle crossings at U.S. land 
            borders with Canada and Mexico. Integrated with license plate readers (LPR), RFID technology, and vehicle 
            registration databases, ATS-L provides real-time risk assessment of vehicles and occupants at land ports of 
            entry, enabling CBP officers to identify high-risk crossings while facilitating legitimate travel and commerce 
            at the world's busiest land borders.
          </Typography>
        </CardContent>
      </Card>

      {/* 5Ws Summary */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Subsystem Overview: The 5Ws
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="ATS-Land 5Ws Overview">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '15%', bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="What is ATS-Land"
                >
                  What
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    A <strong>land border vehicle targeting platform</strong> that assesses risk for <strong>private vehicles, 
                    occupants, and cargo</strong> crossing at land ports of entry. Integrates <strong>license plate reader (LPR) 
                    data, RFID technology, vehicle registration records, and traveler history</strong> to generate <strong>real-time 
                    risk scores</strong> for vehicles approaching primary inspection lanes.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Who uses ATS-Land"
                >
                  Who
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Used by <strong>CBP Officers (CBPOs) at land ports of entry</strong>, <strong>Border Patrol agents</strong>, 
                    and <strong>National Targeting Center (NTC) analysts</strong>. Primary users are <strong>primary inspection 
                    officers</strong> making real-time admissibility decisions and <strong>secondary inspection officers</strong> 
                    conducting detailed examinations of high-risk vehicles.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="When was ATS-Land deployed"
                >
                  When
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Deployed in <strong>2005-2007</strong> as part of Western Hemisphere Travel Initiative. Major enhancements 
                    with <strong>RFID integration (2008-2010)</strong>, <strong>LPR expansion (2012-2015)</strong>, and 
                    <strong>predictive analytics (2018-2020)</strong>. Operates <strong>24/7/365</strong> with <strong>real-time 
                    processing</strong> as vehicles approach inspection booths.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Where is ATS-Land deployed"
                >
                  Where
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Deployed at <strong>119 land ports of entry</strong> along the <strong>U.S.-Mexico border</strong> (San Ysidro, 
                    El Paso, Laredo, Brownsville) and <strong>U.S.-Canada border</strong> (Detroit, Buffalo, Blaine, Portal). 
                    Integrated with <strong>SENTRI/NEXUS trusted traveler lanes</strong> and <strong>FAST commercial vehicle 
                    programs</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Why ATS-Land exists"
                >
                  Why
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    To provide <strong>real-time risk assessment of land border crossings</strong> at the world's busiest 
                    international borders. Processes over <strong>180 million private vehicle crossings annually</strong>, 
                    identifying <strong>narcotics smuggling, human trafficking, immigration violations, and wanted fugitives</strong> 
                    while facilitating legitimate cross-border travel and commerce critical to U.S.-Mexico and U.S.-Canada trade.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Core Capabilities */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Core Targeting Capabilities
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CarIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Vehicle Risk Assessment
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Real-time risk scoring for vehicles approaching inspection.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>LPR Integration:</strong> Automatic license plate capture and analysis</Typography></li>
                <li><Typography variant="body2"><strong>RFID Reading:</strong> SENTRI/NEXUS card detection and validation</Typography></li>
                <li><Typography variant="body2"><strong>Vehicle History:</strong> Past crossings, violations, and seizures</Typography></li>
                <li><Typography variant="body2"><strong>Registration Check:</strong> DMV records and ownership verification</Typography></li>
                <li><Typography variant="body2"><strong>Pattern Analysis:</strong> Unusual crossing times and frequencies</Typography></li>
                <li><Typography variant="body2"><strong>Stolen Vehicle:</strong> NCIC stolen vehicle database matching</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Occupant Vetting
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Risk assessment of vehicle occupants and travelers.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Traveler History:</strong> Past crossings and immigration status</Typography></li>
                <li><Typography variant="body2"><strong>Watchlist Matching:</strong> Criminal warrants and wanted persons</Typography></li>
                <li><Typography variant="body2"><strong>Document Verification:</strong> Passport, visa, and border crossing card validation</Typography></li>
                <li><Typography variant="body2"><strong>Biometric Matching:</strong> Facial recognition at inspection booths</Typography></li>
                <li><Typography variant="body2"><strong>Associate Analysis:</strong> Travel companions and known associates</Typography></li>
                <li><Typography variant="body2"><strong>Immigration Compliance:</strong> Visa overstays and entry violations</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <StorageIcon sx={{ fontSize: 40, color: '#D32F2F', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                  Data Integration
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Consolidates data from multiple law enforcement sources.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>TECS:</strong> Treasury Enforcement Communications System</Typography></li>
                <li><Typography variant="body2"><strong>NCIC:</strong> National Crime Information Center databases</Typography></li>
                <li><Typography variant="body2"><strong>State DMV:</strong> Vehicle registration and driver license records</Typography></li>
                <li><Typography variant="body2"><strong>ADIS:</strong> Arrival and Departure Information System</Typography></li>
                <li><Typography variant="body2"><strong>IDENT:</strong> DHS biometric database</Typography></li>
                <li><Typography variant="body2"><strong>Intelligence Feeds:</strong> DEA, FBI, ICE investigative data</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Operational Support
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Real-time decision support for CBP officers.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Primary Inspection:</strong> Risk scores displayed at booth</Typography></li>
                <li><Typography variant="body2"><strong>Secondary Referrals:</strong> Automated flagging of high-risk vehicles</Typography></li>
                <li><Typography variant="body2"><strong>Exam Guidance:</strong> Targeting notes and search recommendations</Typography></li>
                <li><Typography variant="body2"><strong>Trusted Traveler:</strong> SENTRI/NEXUS expedited processing</Typography></li>
                <li><Typography variant="body2"><strong>Seizure Tracking:</strong> Enforcement action documentation</Typography></li>
                <li><Typography variant="body2"><strong>Wait Time Management:</strong> Lane optimization based on risk levels</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Major Land Ports */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <LocationIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Major Land Ports of Entry
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#003366' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Port of Entry</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Border</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Annual Crossings</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Primary Threats</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>San Ysidro, CA</strong></TableCell>
              <TableCell>U.S.-Mexico</TableCell>
              <TableCell>~17 million vehicles</TableCell>
              <TableCell>Narcotics, human smuggling, contraband</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>El Paso, TX</strong></TableCell>
              <TableCell>U.S.-Mexico</TableCell>
              <TableCell>~12 million vehicles</TableCell>
              <TableCell>Drug trafficking, immigration violations</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Laredo, TX</strong></TableCell>
              <TableCell>U.S.-Mexico</TableCell>
              <TableCell>~8 million vehicles</TableCell>
              <TableCell>Narcotics, currency smuggling</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Detroit, MI</strong></TableCell>
              <TableCell>U.S.-Canada</TableCell>
              <TableCell>~6 million vehicles</TableCell>
              <TableCell>Firearms, immigration violations</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Buffalo, NY</strong></TableCell>
              <TableCell>U.S.-Canada</TableCell>
              <TableCell>~5 million vehicles</TableCell>
              <TableCell>Contraband, wanted fugitives</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Brownsville, TX</strong></TableCell>
              <TableCell>U.S.-Mexico</TableCell>
              <TableCell>~4 million vehicles</TableCell>
              <TableCell>Drug smuggling, human trafficking</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* System Scale */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Scale & Performance
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                180M+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Annual Crossings
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Private vehicle entries
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                119
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Land Ports
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Ports of entry
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                24/7
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Operations
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Real-time processing
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                &lt;2min
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average Wait
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Primary inspection time
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Operational Impact
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Real-Time Targeting:</strong> Risk assessment as vehicles approach</Typography></li>
                <li><Typography variant="body2"><strong>Narcotics Interdiction:</strong> Identify drug smuggling operations</Typography></li>
                <li><Typography variant="body2"><strong>Border Security:</strong> Detect human trafficking and contraband</Typography></li>
                <li><Typography variant="body2"><strong>Fugitive Apprehension:</strong> Identify wanted criminals</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Trade Facilitation:</strong> Expedite low-risk crossings</Typography></li>
                <li><Typography variant="body2"><strong>Trusted Traveler:</strong> SENTRI/NEXUS expedited lanes</Typography></li>
                <li><Typography variant="body2"><strong>Wait Time Reduction:</strong> Optimize inspection resources</Typography></li>
                <li><Typography variant="body2"><strong>Cross-Border Commerce:</strong> Support legitimate trade</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Integration Points */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Key System Integrations
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Chip label="LPR Systems" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">License Plate Readers</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="RFID Readers" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">SENTRI/NEXUS cards</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="TECS" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Law enforcement database</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="NCIC" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Criminal records</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="State DMV" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Vehicle registration</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="IDENT" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Biometric database</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="ADIS" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Travel history</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Intelligence Feeds" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">DEA, FBI, ICE data</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ATSLandSystemDetail;
